import { useMemo, useRef, useState, useId } from 'react';
import type { SelectOption } from './Select';

/**
 * SearchableSelect — a typeahead/combobox single-select for long option lists.
 *
 * A plain <select> is fine for a handful of options, but the consultant / client
 * / property pickers on a Consultant Deal reference contact/client/property lists
 * that can run to hundreds of rows. This atom lets the operator filter by typing
 * while keeping the token design system (dark-first surfaces, brand focus ring).
 *
 * Pure props in / value out — the consumer owns the option list and the selected
 * value; SearchableSelect does no data fetching. `value === null` means unselected.
 *
 * Keyboard: type to filter, ArrowUp/Down to move the highlight, Enter to pick the
 * highlighted option, Escape to close. Mouse: click an option to pick it.
 */

export interface SearchableSelectProps {
  options: SelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  /** When set, a clearing option is rendered at the top of the list (maps to null). */
  emptyLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  'aria-label'?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Search…',
  emptyLabel,
  disabled,
  invalid,
  id,
  'aria-label': ariaLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listId = useId();

  const selected = useMemo(
    () => options.find(o => String(o.value) === String(value)) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // The clearing row (index 0) is prepended when emptyLabel is set.
  const rows: Array<{ value: string | null; label: string }> = useMemo(() => {
    const base = filtered.map(o => ({ value: String(o.value), label: o.label }));
    return emptyLabel ? [{ value: null, label: emptyLabel }, ...base] : base;
  }, [filtered, emptyLabel]);

  function openList() {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    setHighlight(0);
  }

  function pick(row: { value: string | null; label: string }) {
    onChange(row.value);
    setOpen(false);
    setQuery('');
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { openList(); return; }
      setHighlight(h => Math.min(h + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && rows[highlight]) { e.preventDefault(); pick(rows[highlight]); }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const inputValue = open ? query : (selected?.label ?? '');

  return (
    <div style={{ position: 'relative' }}>
      <input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        autoComplete="off"
        disabled={disabled}
        value={inputValue}
        placeholder={selected ? selected.label : placeholder}
        onFocus={openList}
        onBlur={() => {
          // Defer close so an option's onClick can fire before the list unmounts.
          blurTimer.current = setTimeout(() => setOpen(false), 120);
        }}
        onChange={e => { setQuery(e.target.value); setOpen(true); setHighlight(emptyLabel ? 1 : 0); }}
        onKeyDown={onKeyDown}
        style={{
          fontFamily: 'inherit',
          fontSize: 'var(--fs-md)',
          padding: '8px 12px',
          border: `1px solid ${invalid ? 'var(--color-status-red)' : 'var(--color-border)'}`,
          borderRadius: 'var(--r-md)',
          background: 'var(--color-card)',
          color: 'var(--color-ink)',
          outline: 'none',
          width: '100%',
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      />
      {open && (
        <ul
          id={listId}
          role="listbox"
          onMouseDown={e => {
            // Keep focus on the input (prevents the blur-close from racing the click).
            e.preventDefault();
            if (blurTimer.current) clearTimeout(blurTimer.current);
          }}
          style={{
            position: 'absolute',
            zIndex: 20,
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            margin: 0,
            padding: 'var(--sp-1)',
            listStyle: 'none',
            maxHeight: 240,
            overflowY: 'auto',
            background: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--shadow-md, 0 8px 24px rgba(0,0,0,0.24))',
          }}
        >
          {rows.length === 0 && (
            <li style={{ padding: 'var(--sp-2) var(--sp-3)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
              No matches
            </li>
          )}
          {rows.map((row, i) => {
            const isSel = row.value === (value ?? null);
            const isHi = i === highlight;
            return (
              <li key={row.value ?? '__empty__'} role="option" aria-selected={isSel}>
                <button
                  type="button"
                  onClick={() => pick(row)}
                  onMouseEnter={() => setHighlight(i)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 'var(--sp-2) var(--sp-3)',
                    borderRadius: 'var(--r-sm)',
                    fontSize: 'var(--fs-sm)',
                    fontFamily: 'inherit',
                    background: isHi ? 'var(--brand-primary-bg)' : 'transparent',
                    color: row.value === null ? 'var(--color-ink-3)' : 'var(--color-ink)',
                    fontWeight: isSel ? 'var(--fw-semibold)' : 400,
                  }}
                >
                  {row.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
